export const BODY_TYPE_CATEGORIES = [
  "SUV",
  "Sedan",
  "Coupe",
  "Convertible",
  "Hatchback",
  "Wagon",
  "Van",
] as const;

export type BodyTypeCategory = (typeof BODY_TYPE_CATEGORIES)[number];

export const BODY_TYPE_LABELS: Record<BodyTypeCategory, { en: string; ar: string }> = {
  SUV: { en: "SUV", ar: "دفع رباعي" },
  Sedan: { en: "Sedan", ar: "سيدان" },
  Coupe: { en: "Coupe", ar: "كوبيه" },
  Convertible: { en: "Cab", ar: "كاب" },
  Hatchback: { en: "Hatchback", ar: "هاتشباك" },
  Wagon: { en: "Wagon", ar: "واجن" },
  Van: { en: "Van", ar: "فان" },
};

export const BODY_TYPE_LEGACY_LABELS: Record<string, { en: string; ar: string }> = {
  HalfKombi: { en: "Half Kombi", ar: "هاف كومبي" },
};

const BODY_TYPE_ALIASES: Record<BodyTypeCategory, string[]> = {
  SUV: ["suv", "دفع رباعي", "جيب", "crossover", "4x4"],
  Sedan: ["sedan", "سيدان", "saloon"],
  Coupe: ["coupe", "coupé", "كوبيه"],
  Convertible: [
    "convertible",
    "cab",
    "cabrio",
    "cabriolet",
    "كابريوليه",
    "كابريو",
    "كاب",
  ],
  Hatchback: ["hatchback", "هاتشباك", "هاتش"],
  Wagon: ["wagon", "estate", "combi", "واجن", "ستيشن"],
  Van: ["van", "فان", "caravelle", "transporter"],
};

const TITLE_HINTS: { type: BodyTypeCategory; needles: string[] }[] = [
  { type: "Convertible", needles: ["cabriolet", "cabrio", "convertible", "كابريوليه"] },
  { type: "Wagon", needles: ["combi", "wagon", "estate", "avant", "touring"] },
  { type: "Van", needles: ["caravelle", "transporter", "van", "فان"] },
  { type: "Coupe", needles: ["m2", "911", "carrera", "coupe", "coupé"] },
  {
    type: "SUV",
    needles: [
      "rav4",
      "tucson",
      "x5",
      "gle",
      "glc",
      "gla",
      "sorento",
      "sportage",
      "kodiaq",
      "santafe",
      "santa fe",
      "wrangler",
      "cherokee",
      "land cruiser",
      "niro",
      "jeep",
    ],
  },
  {
    type: "Sedan",
    needles: ["330e", "530e", "e200", "e300", "m3", "arteon"],
  },
];

function fold(value: string): string {
  return value.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
}

export function normalizeBodyType(value: string | null | undefined): BodyTypeCategory | null {
  if (!value?.trim()) return null;
  const folded = fold(value);
  for (const category of BODY_TYPE_CATEGORIES) {
    if (folded === fold(category)) return category;
    if (BODY_TYPE_ALIASES[category].some((alias) => folded === fold(alias))) return category;
  }
  return null;
}

export function inferBodyTypeFromTitle(title: string | null | undefined): BodyTypeCategory | null {
  if (!title?.trim()) return null;
  const folded = fold(title);
  for (const { type, needles } of TITLE_HINTS) {
    if (needles.some((needle) => folded.includes(fold(needle)))) return type;
  }
  return normalizeBodyType(title);
}

export function bodyTypeFilterValues(selected: readonly string[]): string[] {
  const out = new Set<string>();
  for (const item of selected) {
    const id = normalizeBodyType(item);
    if (!id) {
      if (item.trim()) out.add(item);
      continue;
    }
    out.add(id);
    for (const alias of BODY_TYPE_ALIASES[id]) out.add(alias);
  }
  return [...out];
}

export function expandBodyTypeFilterValues(
  selected: readonly string[],
  stored: readonly (string | null | undefined)[],
): string[] {
  const wanted = new Set(
    selected.map((item) => normalizeBodyType(item)).filter((item): item is BodyTypeCategory => Boolean(item)),
  );
  return stored.filter((value): value is string => {
    if (!value) return false;
    const id = normalizeBodyType(value);
    return Boolean(id && wanted.has(id));
  });
}

export function bodyTypeLabelAr(key: string | null | undefined): string | null {
  if (!key?.trim()) return null;
  const cat = normalizeBodyType(key) ?? (key as BodyTypeCategory);
  return BODY_TYPE_LABELS[cat as BodyTypeCategory]?.ar ?? BODY_TYPE_LEGACY_LABELS[key]?.ar ?? key;
}

export function bodyTypeLabelBoth(key: string | null | undefined): string | null {
  if (!key?.trim()) return null;
  const cat = normalizeBodyType(key);
  const pair = (cat && BODY_TYPE_LABELS[cat]) || BODY_TYPE_LEGACY_LABELS[key];
  if (!pair) return key;
  if (pair.ar.toLowerCase() === pair.en.toLowerCase()) return pair.ar;
  return `${pair.ar} · ${pair.en}`;
}
